import { InlineNotification, NumberInput, Select, SelectItem, TextInput, Toggle } from '@carbon/react';
import { asProblem, FormModal, isEmptyValue, problemMessage, requiredMarking, useMarkedLabel } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AssignmentsEditor } from './AssignmentsEditor';
import { ProfilesEditor } from './ProfilesEditor';
import { servicesApi, type DeliveryProvider, type DeliveryServiceRow, type ProviderAccount } from './deliveryApi';

/**
 * Add or change a delivery service. For a provider's service the account is chosen from the accounts HO has set up and
 * the provider's own service types are offered; the provider's code and name are copied onto the service, which is what
 * the stores see (ADR 0010). The assignments (where it is offered) and, for a provider's service, its integration profiles (which
 * account books it for which brand or store, ADR 0112) are edited here once the service exists.
 */
export function ServiceDialog({
  providers,
  accounts,
  service,
  onClose,
  onSaved,
}: {
  providers: DeliveryProvider[];
  accounts: ProviderAccount[];
  service?: DeliveryServiceRow;
  onClose(): void;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const label = useMarkedLabel();
  const [code, setCode] = useState(service?.code ?? '');
  const [name, setName] = useState(service?.name ?? '');
  const [kind, setKind] = useState(service?.kind ?? 'external');
  const [accountId, setAccountId] = useState(service?.providerAccountId ?? accounts[0]?.id ?? '');
  const account = accounts.find((a) => a.id === accountId);
  const provider = providers.find((p) => p.id === account?.providerId);
  const [serviceType, setServiceType] = useState(service?.serviceType ?? provider?.serviceTypes[0]?.code ?? '');
  const [feeMode, setFeeMode] = useState(service?.feeMode ?? 'manual');
  const [fixedFee, setFixedFee] = useState(service?.fixedFee == null ? '' : String(service.fixedFee));
  const [sortOrder, setSortOrder] = useState(String(service?.sortOrder ?? 0));
  const [isActive, setIsActive] = useState(service?.isActive ?? true);
  const [savedId, setSavedId] = useState(service?.id);
  const [justCreated, setJustCreated] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const external = kind === 'external';
  const canQuote = Boolean(provider?.capabilities.includes('quote'));
  const fixed = external && feeMode === 'fixed';

  // Every field but the sort order is required, so the sort order is the one marked "(optional)".
  const marking = requiredMarking([
    { required: true },
    { required: true },
    { required: true },
    ...(external ? [{ required: true }, { required: true }, { required: true }] : []),
    ...(fixed ? [{ required: true }] : []),
    { required: false },
  ]);

  const missing = new Set<string>();
  if (isEmptyValue(code)) missing.add('service-code');
  if (isEmptyValue(name)) missing.add('service-name');
  if (external && isEmptyValue(accountId)) missing.add('service-account');
  if (external && isEmptyValue(serviceType)) missing.add('service-type');
  if (fixed && isEmptyValue(fixedFee)) missing.add('service-fixed-fee');
  const touch = (id: string) => setTouched((now) => new Set([...now, id]));
  const invalid = (id: string) => missing.has(id) && (submitted || touched.has(id));
  const requiredText = t('admin.validation.required');

  const save = useMutation({
    mutationFn: async () => {
      const input = {
        code: code.trim(),
        name: name.trim(),
        nameI18n: null,
        kind,
        providerAccountId: external ? accountId : null,
        providerCode: external ? (provider?.code ?? null) : null,
        providerName: external ? (provider?.name ?? null) : null,
        serviceType: external ? serviceType : null,
        feeMode: external ? feeMode : 'manual',
        fixedFee: fixed ? Number(fixedFee) : null,
        requiresHo: external,
        sortOrder: Number(sortOrder) || 0,
        isActive,
      };
      if (savedId) {
        const current = await servicesApi.get(savedId);
        return servicesApi.update(current, input);
      }
      return servicesApi.create(input);
    },
    onSuccess: (saved) => {
      onSaved();
      // A new service stays open so where it is offered can be set; an edit just closes.
      if (service) {
        onClose();
        return;
      }
      setSavedId(saved.id);
      setJustCreated(true);
    },
  });

  const submit = () => {
    setSubmitted(true);
    setJustCreated(false);
    if (missing.size === 0) save.mutate();
  };

  const noAccounts = external && accounts.length === 0;

  return (
    <FormModal
      title={service ? t('admin.edit') : t('admin.create')}
      label={t('delivery.services.title')}
      pending={save.isPending}
      error={save.error ? problemMessage(t, asProblem(save.error)) : null}
      onSubmit={submit}
      onClose={onClose}
    >
      <TextInput
        id="service-code"
        labelText={label(t('delivery.services.columns.code'), true, marking)}
        value={code}
        maxLength={50}
        required
        invalid={invalid('service-code')}
        invalidText={requiredText}
        onBlur={() => touch('service-code')}
        onChange={(e) => setCode(e.target.value)}
      />
      <TextInput
        id="service-name"
        labelText={label(t('delivery.services.columns.name'), true, marking)}
        value={name}
        maxLength={200}
        required
        invalid={invalid('service-name')}
        invalidText={requiredText}
        onBlur={() => touch('service-name')}
        onChange={(e) => setName(e.target.value)}
      />
      <Select
        id="service-kind"
        labelText={label(t('delivery.services.columns.kind'), true, marking)}
        value={kind}
        required
        onChange={(e) => setKind(e.target.value)}
      >
        <SelectItem value="internal" text={t('delivery.services.kinds.internal')} />
        <SelectItem value="external" text={t('delivery.services.kinds.external')} />
      </Select>
      {external && (
        <>
          {noAccounts && (
            <InlineNotification
              kind="warning"
              role="alert"
              lowContrast
              hideCloseButton
              title={t('delivery.services.noAccounts')}
            />
          )}
          <Select
            id="service-account"
            labelText={label(t('delivery.services.account'), true, marking)}
            value={accountId}
            required
            invalid={noAccounts || invalid('service-account')}
            invalidText={noAccounts ? t('delivery.services.noAccounts') : requiredText}
            onBlur={() => touch('service-account')}
            onChange={(e) => {
              setAccountId(e.target.value);
              const next = providers.find((p) => p.id === accounts.find((a) => a.id === e.target.value)?.providerId);
              setServiceType(next?.serviceTypes[0]?.code ?? '');
              if (!next?.capabilities.includes('quote') && feeMode === 'quote') setFeeMode('manual');
            }}
          >
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id} text={`${a.name} (${a.providerName})`} />
            ))}
          </Select>
          <Select
            id="service-type"
            labelText={label(t('delivery.services.serviceType'), true, marking)}
            value={serviceType}
            required
            invalid={invalid('service-type')}
            invalidText={requiredText}
            onBlur={() => touch('service-type')}
            onChange={(e) => setServiceType(e.target.value)}
          >
            {(provider?.serviceTypes ?? []).map((s) => (
              <SelectItem key={s.code} value={s.code} text={s.name} />
            ))}
          </Select>
          <Select
            id="service-fee-mode"
            labelText={label(t('delivery.services.columns.fee'), true, marking)}
            value={feeMode}
            required
            onChange={(e) => setFeeMode(e.target.value)}
          >
            <SelectItem value="manual" text={t('delivery.services.feeModes.manual')} />
            <SelectItem value="fixed" text={t('delivery.services.feeModes.fixed')} />
            {canQuote && <SelectItem value="quote" text={t('delivery.services.feeModes.quote')} />}
          </Select>
          {fixed && (
            <NumberInput
              id="service-fixed-fee"
              label={label(t('delivery.services.fixedFee'), true, marking)}
              min={0}
              value={fixedFee === '' ? '' : Number(fixedFee)}
              allowEmpty
              required
              invalid={invalid('service-fixed-fee')}
              invalidText={requiredText}
              onBlur={() => touch('service-fixed-fee')}
              onChange={(_, state) => setFixedFee(state.value === '' ? '' : String(state.value))}
            />
          )}
        </>
      )}
      <NumberInput
        id="service-sort"
        label={label(t('delivery.services.sortOrder'), false, marking)}
        value={Number(sortOrder)}
        onChange={(_, state) => setSortOrder(String(state.value))}
      />
      <Toggle
        id="service-active"
        labelText={t('delivery.providers.dialog.active')}
        labelA={t('delivery.services.assignments.off')}
        labelB={t('delivery.services.assignments.on')}
        toggled={isActive}
        onToggle={setIsActive}
      />
      {justCreated && (
        <InlineNotification
          kind="success"
          role="status"
          lowContrast
          hideCloseButton
          title={t('delivery.services.created')}
        />
      )}
      {savedId && <AssignmentsEditor serviceId={savedId} />}
      {savedId && external && provider && <ProfilesEditor serviceId={savedId} provider={provider} accounts={accounts} />}
    </FormModal>
  );
}
